// EM2c Hex Grid Placement System
// Handles building placement on a hex grid with footprint validation,
// connector alignment, and preview colors

import { HEX_SIZE, axialToWorld, worldToAxial, getNeighbors, rotateFootprint, hexDistance, HEX_DIRECTIONS } from "./hexgrid";

// Placement validity states
export const PLACEMENT = {
  VALID: "valid",         // green - can place
  BLOCKED: "blocked",     // red - occupied or invalid terrain
  WARNING: "warning",     // yellow - valid but missing recommended connection
  CONNECTOR: "connector", // blue - compatible connector alignment
  TERRAIN: "terrain",     // orange - terrain needs preparation
};

// Check if a building footprint can be placed at origin hex with given rotation
export function validatePlacement(grid, building, originQ, originR, rotation) {
  const footprint = rotateFootprint(building.footprintHexes, rotation);
  const results = footprint.map(({ q, r }) => {
    const hexKey = `${originQ + q},${originR + r}`;
    const hex = grid.get(hexKey);
    if (!hex) return { ok: false, reason: "Out of bounds", hex: null };
    if (!hex.buildable) return { ok: false, reason: "Terrain not buildable", hex };
    if (hex.occupied || hex.buildingId) return { ok: false, reason: "Hex occupied", hex };
    if (hex.slope > building.maxSlope) return { ok: false, reason: "Slope too steep", hex };
    if (hex.hazard) return { ok: false, reason: "Hazard zone", hex };
    return { ok: true, hex };
  });

  const invalid = results.find(r => !r.ok);
  if (invalid) {
    return { valid: false, reason: invalid.reason, placement: PLACEMENT.BLOCKED };
  }

  // Check if any connectors align with nearby buildings
  const hasConnector = checkConnectorAlignment(grid, building, originQ, originR, rotation);
  
  // Check if building needs a resource deposit (mining/water)
  if (building.requiresDeposit) {
    const deposit = findNearestDeposit(grid, footprint, originQ, originR, building.requiresDeposit);
    if (!deposit) {
      return { valid: false, reason: `Requires nearby ${building.requiresDeposit} deposit`, placement: PLACEMENT.BLOCKED };
    }
  }

  // Check drone access - must be within drone control radius of command center
  const command = findCommandCenter(grid);
  if (command) {
    const dist = hexDistance(originQ, originR, command.q, command.r);
    if (dist > 30) { // 30 hexes * 4 units = 120 world units
      return { valid: false, reason: "Beyond command control radius", placement: PLACEMENT.BLOCKED };
    }
  }

  // Valid placement - check if connectors align
  if (hasConnector) {
    return { valid: true, placement: PLACEMENT.CONNECTOR };
  }

  return { valid: true, placement: PLACEMENT.VALID };
}

// Check if building connectors align with any neighboring building
function checkConnectorAlignment(grid, building, originQ, originR, rotation) {
  const neighbors = getNeighbors(originQ, originR);
  for (const { q, r } of neighbors) {
    const hexKey = `${q},${r}`;
    const hex = grid.get(hexKey);
    if (!hex || !hex.buildingId) continue;
    
    // Check if any port types match
    const otherBuilding = findBuildingById(hex.buildingId);
    if (!otherBuilding) continue;
    
    // Check power port compatibility
    if (building.powerPorts?.length > 0 && otherBuilding.powerPorts?.length > 0) {
      return true;
    }
    // Check corridor compatibility
    if (building.corridorPorts?.length > 0 && otherBuilding.corridorPorts?.length > 0) {
      return true;
    }
    // Check water compatibility
    if (building.waterPorts?.length > 0 && otherBuilding.waterPorts?.length > 0) {
      return true;
    }
    // Check oxygen compatibility
    if (building.oxygenPorts?.length > 0 && otherBuilding.oxygenPorts?.length > 0) {
      return true;
    }
    // Check data compatibility
    if (building.dataPorts?.length > 0 && otherBuilding.dataPorts?.length > 0) {
      return true;
    }
  }
  return false;
}

// Find a resource deposit of given type directly under the building's
// footprint. Building must sit EXACTLY on a deposit hex — adjacent hexes
// no longer count, so only the true deposit tile shows as buildable.
function findNearestDeposit(grid, footprint, originQ, originR, depositType) {
  for (const { q, r } of footprint) {
    const hexQ = originQ + q;
    const hexR = originR + r;
    const selfHex = grid.get(`${hexQ},${hexR}`);
    if (selfHex?.hasDeposit === depositType) return selfHex;
  }
  return null;
}

// Find the command center in the grid
let _commandCenter = null;
export function setCommandCenter(hex) { _commandCenter = hex; }
function findCommandCenter() { return _commandCenter; }

// Find building by ID (placeholder - store will provide this)
let _buildingLookup = null;
export function setBuildingLookup(fn) { _buildingLookup = fn; }
function findBuildingById(id) {
  if (!_buildingLookup) return null;
  return _buildingLookup(id);
}

// Get the world-space corners of a hex at given axial coords
export function getHexCorners(q, r) {
  const { x, z } = axialToWorld(q, r);
  const corners = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i); // flat-top: 0, 60, 120, 180, 240, 300
    corners.push({
      x: x + HEX_SIZE * Math.cos(angle),
      z: z + HEX_SIZE * Math.sin(angle),
    });
  }
  return corners;
}

// Get the edge midpoint between two hexes
export function getEdgeMidpoint(q1, r1, q2, r2) {
  const p1 = axialToWorld(q1, r1);
  const p2 = axialToWorld(q2, r2);
  return {
    x: (p1.x + p2.x) / 2,
    z: (p1.z + p2.z) / 2,
  };
}

// Get connector position at a specific edge direction of a building's origin hex
export function getConnectorPosition(q, r, edgeDir) {
  const center = axialToWorld(q, r);
  const angle = (Math.PI / 180) * (60 * edgeDir);
  return {
    x: center.x + HEX_SIZE * Math.cos(angle),
    z: center.z + HEX_SIZE * Math.sin(angle),
  };
}

// Mark hexes as occupied by a building
export function occupyHexes(grid, footprint, originQ, originR, rotation, buildingId) {
  const rotated = rotateFootprint(footprint, rotation);
  for (const { q, r } of rotated) {
    const hexKey = `${originQ + q},${originR + r}`;
    const hex = grid.get(hexKey);
    if (hex) {
      hex.occupied = true;
      hex.buildingId = buildingId;
    }
  }
}

// Free hexes when a building is demolished
export function freeHexes(grid, footprint, originQ, originR, rotation) {
  const rotated = rotateFootprint(footprint, rotation);
  for (const { q, r } of rotated) {
    const hexKey = `${originQ + q},${originR + r}`;
    const hex = grid.get(hexKey);
    if (hex) {
      hex.occupied = false;
      hex.buildingId = null;
    }
  }
}

// Check if two buildings are adjacent (share at least one edge)
export function areAdjacent(grid, b1, b2) {
  // Check all hexes of b1 against all hexes of b2
  const hexes1 = getBuildingHexes(b1);
  const hexes2 = getBuildingHexes(b2);
  for (const h1 of hexes1) {
    for (const h2 of hexes2) {
      const dist = hexDistance(h1.q, h1.r, h2.q, h2.r);
      if (dist === 1) return true;
    }
  }
  return false;
}

// Get all hex coords occupied by a building
function getBuildingHexes(building) {
  const rotated = rotateFootprint(building.footprintHexes || [], building.rotation || 0);
  return rotated.map(({ q, r }) => ({
    q: building.q + q,
    r: building.r + r,
  }));
}

// Get all buildings adjacent to a given building
export function getAdjacentBuildings(grid, building, allBuildings) {
  const result = [];
  const hexes = getBuildingHexes(building);
  const checked = new Set();
  for (const { q, r } of hexes) {
    const neighbors = getNeighbors(q, r);
    for (const { q: nq, r: nr } of neighbors) {
      const key = `${nq},${nr}`;
      if (checked.has(key)) continue;
      checked.add(key);
      const hex = grid.get(key);
      if (!hex || !hex.buildingId) continue;
      const other = allBuildings.find(b => b.id === hex.buildingId);
      if (other && other.id !== building.id && !result.find(b => b.id === other.id)) {
        result.push(other);
      }
    }
  }
  return result;
}

// Determine which connector types are compatible between two buildings
export function getCompatibleConnectors(b1, b2) {
  const compatible = [];
  const types = [
    { name: "corridor", p1: "corridorPorts", p2: "corridorPorts" },
    { name: "power", p1: "powerPorts", p2: "powerPorts" },
    { name: "water", p1: "waterPorts", p2: "waterPorts" },
    { name: "oxygen", p1: "oxygenPorts", p2: "oxygenPorts" },
    { name: "data", p1: "dataPorts", p2: "dataPorts" },
  ];
  for (const t of types) {
    if (b1[t.p1]?.length > 0 && b2[t.p2]?.length > 0) {
      compatible.push(t.name);
    }
  }
  return compatible;
}
